/**
 * Your own account — scope-free, because this is you, not admin management.
 *
 * Things the screen is careful to be honest about:
 *   - **Your email is your login and you cannot change it.** The field is disabled and says who can.
 *   - **TOTP cannot be turned off**, only re-enrolled — and **a superadmin cannot even re-enrol
 *     their own** from here (doc 05-admin/02 §3: break-glass recovery is an operational runbook,
 *     not a button, precisely because the account that can reset everyone else's must not be
 *     resettable by whoever is holding its session).
 *   - **Two alerts are locked on.** A safety page and an audit-chain break reach every admin
 *     regardless of preference, and the toggle is disabled with that as its reason.
 *   - **You cannot delete your own account.** You request deactivation; a superadmin acts, and your
 *     audit trail stays forever.
 */
import { useState } from 'react';
import { Button, Icon, StatusChip } from '../components/primitives';
import type { IconName } from '../components/icons';
import { Input, Select, Toggle } from '../components/forms';
import { KV } from '../components/entities';
import { ConfirmAction, Section } from '../components/kit';
import { SHORTCUTS } from '../app/furniture';
import { MIN_PASSWORD_LENGTH, passwordChecks, passwordReady } from '../lib/password';
import { useConsole } from '../app/console';
import { currentAdmin } from '../lib/auth/session';
import {
  useChangePassword,
  useConfirmReEnrol,
  useMe,
  useNotificationPrefs,
  useRecoveryCodeStatus,
  useRegenerateRecoveryCodes,
  useRequestDeactivation,
  useOwnSessions,
  useRevokeSession,
  useSaveNotificationPrefs,
  useSignOutOtherDevices,
  useStartReEnrol,
  useUpdateProfile,
} from '../lib/api/hooks/account';
import type { ProfileUpdate } from '../lib/api/hooks/account';
import { errorMessage } from '../lib/api/client';
import {
  ACCOUNT_TABS,
  DENSITIES,
  DISPLAY_PREFS,
  LANGUAGES,
  THEME_CHOICES,
  TIMEZONES,
  TOPIC_COPY,
  TOPIC_GROUPS,
} from './accountCopy';
import type { Choice } from './accountCopy';
import { formatDateTime } from '../lib/datetime';

/** Hoisted so the icon generator sees both glyphs. */
const DEVICE_ICON = { phone: 'smartphone', desktop: 'monitor' } satisfies Record<string, IconName>;

/** The server reports a platform string; anything that is not obviously a phone gets a desktop. */
export function deviceIcon(platform: string | null | undefined): IconName {
  return /ios|android|iphone|mobile/i.test(platform ?? '') ? DEVICE_ICON.phone : DEVICE_ICON.desktop;
}

/**
 * Group the server's topics for the screen.
 *
 * ⛔ Keyed on the TOPIC, never on the label. Two rows could share wording, and a label-keyed map
 * would silently merge them — the same defect the coverage grid had before E36. A topic this
 * build has no copy for still appears, under its key: an alert quietly missing from this page is
 * how somebody stops receiving one without ever choosing to.
 */
export function groupTopics<T extends { topic: string }>(
  topics: readonly T[],
): { group: string; rows: T[] }[] {
  const byGroup = new Map<string, T[]>();
  for (const row of topics) {
    const group = TOPIC_COPY[row.topic]?.group ?? 'Other alerts';
    byGroup.set(group, [...(byGroup.get(group) ?? []), row]);
  }
  /* Sorted from the entries rather than re-fetching each key: a `byGroup.get(g) ?? []` after
     reading `g` out of the same map is a fallback nothing can reach, and an unreachable branch is
     a line the coverage gate can only be argued past. Unknown groups sort to the end. */
  const rank = (group: string): number => {
    const at = TOPIC_GROUPS.indexOf(group);
    return at === -1 ? TOPIC_GROUPS.length : at;
  };
  /* No tiebreak: every unknown group collapses to the one literal 'Other alerts', so two
     distinct groups can never share a rank. A `localeCompare` fallback here would be a branch
     nothing could reach. */
  return [...byGroup.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([group, rows]) => ({ group, rows }));
}

/** The words for a topic, falling back to its key so an unknown one is visible, not invisible. */
export function topicCopy(topic: string): { label: string; note: string } {
  return TOPIC_COPY[topic] ?? { label: topic, note: 'This alert is newer than this console build.' };
}

/**
 * A date the way this product means dates.
 *
 * ⛔ Pinned to `Asia/Kolkata` rather than left to the browser. IST is assumed everywhere in the
 * product and never printed next to a time, so an enrolment date rendered in whatever timezone
 * the operator's laptop is set to would silently disagree with every other date on the console.
 */
export function enrolledOn(iso: string | null | undefined): string {
  if (!iso) return 'date unknown';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return 'date unknown';
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(parsed);
}

/** Match a stored value back to its display label, and back again on the way out. */
export function labelFor(choices: readonly Choice[], value: string | null | undefined): string {
  return choices.find((c) => c.value === value)?.label ?? '';
}

export function valueFor(choices: readonly Choice[], label: string): string {
  return choices.find((c) => c.label === label)?.value ?? '';
}

export function MyAccount() {
  const { scope, go, toast } = useConsole();
  /* Whoever actually signed in. `ME` still supplies the record around them — enrolment date,
     recovery-code count — but the name, email and scope on a page titled "Your account" have to
     be yours, or the page is describing somebody else. */
  /* Non-null: this screen renders inside the shell, and the route guard means the shell only
     renders for someone who is signed in. */
  const me = currentAdmin()!;
  /* Live, from `GET /v1/auth/sessions`. This list is the reason the screen exists: the first
     thing anyone does after losing a phone is come here to end the session on it. */
  const sessions = useOwnSessions();
  const revoke = useRevokeSession();
  const signOutOthers = useSignOutOtherDevices();
  const changePassword = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [passwordCode, setPasswordCode] = useState('');
  const others = (sessions.data?.items ?? []).filter((s) => !s.current);
  /* Doc 02 §3 — self-reset is the one authenticator action a superadmin does not get. The SERVER
     refuses it too (`superadmin_re_enrolment_refused`); this only avoids offering a door that
     will not open. Client RBAC is UX, the server is the boundary. */
  const canReEnrolTotp = scope !== 'superadmin';
  const [tab, setTab] = useState('account');
  const [name, setName] = useState(me.name);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [confirm, setConfirm] = useState<string | null>(null);

  /* ── The profile, live ────────────────────────────────────────────────────
     `draft` holds ONLY the fields the operator has touched, which is exactly what the PATCH
     wants: the server leaves an omitted field alone and clears an explicit null, so sending the
     whole form would overwrite values nobody edited. Everything else reads through to the
     server's answer. */
  const profile = useMe();
  const saveProfile = useUpdateProfile();
  const [draft, setDraft] = useState<ProfileUpdate>({});
  const field = (key: keyof ProfileUpdate): string =>
    (draft[key] ?? profile.data?.[key] ?? '') as string;
  const edit = (key: keyof ProfileUpdate) => (value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const dirty = Object.keys(draft).length > 0;

  async function commitProfile(): Promise<void> {
    try {
      /* An empty string from a form means "no value". Sent as `null` so the server clears the
         column, rather than as `''` which a closed-set field would rightly refuse. */
      await saveProfile.mutateAsync(
        Object.fromEntries(
          Object.entries(draft).map(([k, v]) => [k, v === '' ? null : v]),
        ) as ProfileUpdate,
      );
      setDraft({});
      toast({ tone: 'success', message: 'Saved' });
    } catch (error) {
      toast({ tone: 'error', message: errorMessage(error) });
    }
  }

  /* ── Alerts, live ─────────────────────────────────────────────────────────
     The matrix is the SERVER's, including which rows are locked and why. */
  const prefs = useNotificationPrefs();
  const savePrefs = useSaveNotificationPrefs();
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const topics = (prefs.data?.topics ?? []).map((t) => ({
    ...t,
    is_enabled: pending[t.topic] ?? t.is_enabled,
  }));

  async function commitPrefs(): Promise<void> {
    try {
      await savePrefs.mutateAsync(pending);
      setPending({});
      toast({ tone: 'success', message: 'Alert preferences saved' });
    } catch (error) {
      toast({ tone: 'error', message: errorMessage(error) });
    }
  }

  /* ── Moving the authenticator ─────────────────────────────────────────────
     Two steps, and the order matters. `stepup` re-proves the OLD authenticator; only then does
     the server mint a pending secret. The old code keeps working throughout — abandoning this
     half-way costs nothing, which is the whole reason the server holds the new secret aside
     instead of overwriting the live one. */
  const startReEnrol = useStartReEnrol();
  const confirmReEnrol = useConfirmReEnrol();
  const [reEnrolStep, setReEnrolStep] = useState<'idle' | 'stepup' | 'scan'>('idle');
  const [reEnrolUri, setReEnrolUri] = useState('');
  const [reEnrolPassword, setReEnrolPassword] = useState('');
  const [reEnrolCode, setReEnrolCode] = useState('');
  const [freshCodes, setFreshCodes] = useState<readonly string[]>([]);

  function endReEnrol(): void {
    setReEnrolStep('idle');
    setReEnrolUri('');
    setReEnrolPassword('');
    setReEnrolCode('');
  }

  async function beginReEnrol(): Promise<void> {
    try {
      const started = await startReEnrol.mutateAsync({
        current_password: reEnrolPassword,
        totp_code: reEnrolCode,
      });
      setReEnrolUri(started.otpauth_uri);
      setReEnrolPassword('');
      setReEnrolCode('');
      setReEnrolStep('scan');
    } catch (error) {
      toast({ tone: 'error', message: errorMessage(error) });
    }
  }

  async function finishReEnrol(): Promise<void> {
    try {
      const done = await confirmReEnrol.mutateAsync(reEnrolCode);
      setFreshCodes(done.recovery_codes);
      endReEnrol();
      toast({ tone: 'success', message: 'Your new authenticator is active' });
    } catch (error) {
      toast({ tone: 'error', message: errorMessage(error) });
    }
  }

  /* ── Recovery codes ───────────────────────────────────────────────────────
     The count is the server's `COUNT(*)`. Regenerating re-proves both factors first, exactly like
     moving the authenticator, and the new set is shown once. */
  const recovery = useRecoveryCodeStatus();
  const regenerate = useRegenerateRecoveryCodes();
  const [codesStep, setCodesStep] = useState<'idle' | 'stepup'>('idle');
  const [codesPassword, setCodesPassword] = useState('');
  const [codesCode, setCodesCode] = useState('');
  const requestDeactivation = useRequestDeactivation();

  function endRegenerate(): void {
    setCodesStep('idle');
    setCodesPassword('');
    setCodesCode('');
  }

  async function finishRegenerate(): Promise<void> {
    try {
      const done = await regenerate.mutateAsync({
        current_password: codesPassword,
        totp_code: codesCode.trim(),
      });
      setFreshCodes(done.recovery_codes);
      endRegenerate();
      toast({ tone: 'success', message: 'New recovery codes generated · save them now' });
    } catch (error) {
      toast({ tone: 'error', message: errorMessage(error) });
    }
  }

  const checks = passwordChecks(password, confirmation);
  const ready = passwordReady(checks);

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 240px) minmax(0, 1fr)', gap: 'var(--sp-xl)', alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 2, position: 'sticky', top: 'var(--sp-lg)' }}>
          {ACCOUNT_TABS.map((t) => {
            const on = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                aria-current={on ? 'page' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-sm)',
                  height: 38,
                  padding: '0 var(--sp-md)',
                  border: 'none',
                  borderRadius: 'var(--r-tile)',
                  cursor: 'pointer',
                  background: on ? 'var(--c-saffron-50)' : 'transparent',
                  color: on ? 'var(--c-saffron-text)' : 'var(--c-ink2)',
                  font: 'inherit',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--fs-admin-ui)',
                  fontWeight: on ? 'var(--fw-semibold)' : 'var(--fw-regular)',
                  textAlign: 'left',
                }}
              >
                <Icon name={t.icon} size={16} />
                <span style={{ flex: 1 }}>{t.label}</span>
                {on && <Icon name="chevron-right" size={14} />}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'grid', gap: 'var(--sp-lg)', minWidth: 0 }}>
          {tab === 'account' && (
            <>
              <Section
                title="Profile"
                note="How you appear across the console and in every audit entry"
                actions={
                  <Button size="sm" disabled={!dirty || saveProfile.isPending} onClick={commitProfile}>
                    {saveProfile.isPending ? 'Saving…' : 'Save changes'}
                  </Button>
                }
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--sp-md)' }}>
                  <Input density="admin" label="Full name" value={name} onChange={setName} />
                  <Input
                    density="admin"
                    label="Display name"
                    value={field('display_name')}
                    onChange={edit('display_name')}
                    helper="Used in queues and toasts. Leave it empty to use your full name."
                  />
                </div>
                <Input
                  density="admin"
                  label="Email"
                  value={me.email}
                  disabled
                  helper="Your login. Only a superadmin can change it — ask in #ops-access."
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--sp-md)' }}>
                  <Input
                    density="admin"
                    label="Phone"
                    value={field('phone')}
                    onChange={edit('phone')}
                    helper="For the on-call rota and safety pages."
                  />
                  <div style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>Scopes</span>
                    <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap', alignItems: 'center', minHeight: 40 }}>
                      {/* The live scope, not the fixture's: this page must agree with the console
                          you are actually looking at, or the TOTP rule below reads as a bug. */}
                      <StatusChip tone={scope === 'superadmin' ? 'error' : 'info'} label={scope} icon="shield" />
                      <Button size="sm" variant="ghost" onClick={() => go('admins')}>
                        See the scope matrix
                      </Button>
                    </div>
                  </div>
                </div>
              </Section>

              <Section
                title="Locale"
                note="Only affects how you see dates and numbers — stored values never change"
                actions={
                  <Button size="sm" disabled={!dirty || saveProfile.isPending} onClick={commitProfile}>
                    Save
                  </Button>
                }
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--sp-md)' }}>
                  {/* The stored value is an IANA name and a locale code; these controls speak in
                      words. The mapping lives at this seam and nowhere else. */}
                  <Select
                    density="admin"
                    label="Timezone"
                    value={labelFor(TIMEZONES, field('timezone'))}
                    options={TIMEZONES.map((c) => c.label)}
                    placeholder="Not set"
                    onChange={(label) => edit('timezone')(valueFor(TIMEZONES, label))}
                  />
                  <Select
                    density="admin"
                    label="Language"
                    value={labelFor(LANGUAGES, field('language'))}
                    options={LANGUAGES.map((c) => c.label)}
                    placeholder="Not set"
                    onChange={(label) => edit('language')(valueFor(LANGUAGES, label))}
                  />
                </div>
                <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                  IST is assumed everywhere in the product and never printed next to a time. Money
                  stays ₹ with Indian grouping in both languages.
                </span>
              </Section>

              <Section title="Danger zone" note="Neither action can be undone by you">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-md)',
                    padding: 'var(--sp-md)',
                    border: '1px solid var(--c-border)',
                    borderRadius: 'var(--r-tile)',
                    background: 'var(--c-surface2)',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: 1, display: 'grid', minWidth: 220 }}>
                    <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink)' }}>
                      Sign out of every other device
                    </span>
                    <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                      Revokes {others.length} other {others.length === 1 ? 'session' : 'sessions'}. This one stays.
                    </span>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => setConfirm('signout')}>
                    Sign out others
                  </Button>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-md)',
                    padding: 'var(--sp-md)',
                    border: '1px solid var(--c-error)',
                    borderRadius: 'var(--r-tile)',
                    background: 'var(--c-error-bg)',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: 1, display: 'grid', minWidth: 220 }}>
                    <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-error-text)' }}>
                      Request deactivation
                    </span>
                    <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-error-text)' }}>
                      You cannot delete your own account. A superadmin deactivates it; your audit
                      trail stays forever.
                      {profile.data?.deactivation_requested_at
                        ? ` Requested ${enrolledOn(profile.data.deactivation_requested_at)} — waiting for a superadmin.`
                        : ''}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={Boolean(profile.data?.deactivation_requested_at)}
                    onClick={() => setConfirm('deactivate')}
                  >
                    {profile.data?.deactivation_requested_at ? 'Requested' : 'Request'}
                  </Button>
                </div>
              </Section>
            </>
          )}

          {tab === 'security' && (
            <>
              <Section
                title="Change password"
                note="Argon2id on the server · never reused from another system"
                actions={
                  <Button
                    size="sm"
                    disabled={!ready || !currentPassword || !passwordCode || changePassword.isPending}
                    onClick={() => setConfirm('password')}
                  >
                    {changePassword.isPending ? 'Updating…' : 'Update password'}
                  </Button>
                }
              >
                <Input
                  density="admin"
                  type="password"
                  label="Current password"
                  value={currentPassword}
                  onChange={setCurrentPassword}
                />
                <Input
                  density="admin"
                  label="Authenticator code"
                  value={passwordCode}
                  onChange={setPasswordCode}
                  inputMode="numeric"
                  placeholder="6 digits"
                  helper="Both factors again — a walked-away-from console should not be a password change."
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--sp-md)' }}>
                  <Input
                    density="admin"
                    type="password"
                    label="New password"
                    value={password}
                    onChange={setPassword}
                    placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  />
                  <Input
                    density="admin"
                    type="password"
                    label="Confirm new password"
                    value={confirmation}
                    onChange={setConfirmation}
                    placeholder="Re-enter the new password"
                  />
                </div>
                <div
                  style={{
                    border: '1px solid var(--c-border)',
                    borderRadius: 'var(--r-tile)',
                    background: 'var(--c-sunk)',
                    padding: 'var(--sp-md)',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 6,
                  }}
                >
                  {checks.map((c) => (
                    <span
                      key={c.label}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-sm)',
                        fontSize: 'var(--fs-admin-cell)',
                        color: c.ok ? 'var(--c-success-text)' : 'var(--c-ink3)',
                      }}
                    >
                      <Icon name={c.ok ? 'circle-check' : 'circle'} size={14} />
                      {c.label}
                    </span>
                  ))}
                </div>
                <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                  Changing your password signs out your other sessions.
                </span>
              </Section>

              <Section
                title="Two-factor authentication"
                note="Mandatory for every admin — it cannot be turned off, only re-enrolled"
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-md)',
                    padding: 'var(--sp-md)',
                    border: '1px solid var(--c-border)',
                    borderRadius: 'var(--r-tile)',
                    background: 'var(--c-surface2)',
                    flexWrap: 'wrap',
                  }}
                >
                  <Icon name="smartphone" size={18} style={{ color: 'var(--c-saffron)' }} />
                  <div style={{ flex: 1, display: 'grid', minWidth: 200 }}>
                    <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink)' }}>Authenticator app</span>
                    <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                      Time-based codes · issuer “BookMyPandiit Admin” · enrolled{' '}
                      {enrolledOn(profile.data?.totp_enrolled_at)}
                    </span>
                  </div>
                  <StatusChip tone="success" label="Enrolled" />
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!canReEnrolTotp}
                    onClick={() => setConfirm('totp')}
                  >
                    Re-enrol
                  </Button>
                </div>
                {!canReEnrolTotp && (
                  <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                    A superadmin cannot re-enrol their own authenticator here. Break-glass recovery
                    is an operational runbook, not a button — the account that resets everyone
                    else’s must not be resettable by whoever is holding its session.
                  </span>
                )}

                {reEnrolStep === 'stepup' && (
                  <Section title="Prove it is you" note="Your current password and a code from the authenticator you still have" pad>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--sp-md)' }}>
                      <Input
                        density="admin"
                        type="password"
                        /* ⛔ Not "Current password": the password-change form on this same tab
                           already uses that, and two identically-named controls on one screen are
                           ambiguous to a screen reader long before they are ambiguous to a test. */
                        label="Current password to authorise this move"
                        value={reEnrolPassword}
                        onChange={setReEnrolPassword}
                      />
                      <Input
                        density="admin"
                        label="Code from your current authenticator"
                        value={reEnrolCode}
                        onChange={setReEnrolCode}
                        placeholder="123456"
                      />
                    </div>
                    <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                      Your current authenticator keeps working until the new one is confirmed, so
                      stopping here changes nothing.
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
                      <Button
                        size="sm"
                        disabled={!reEnrolPassword || !reEnrolCode || startReEnrol.isPending}
                        onClick={beginReEnrol}
                      >
                        {startReEnrol.isPending ? 'Checking…' : 'Continue'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={endReEnrol}>
                        Cancel
                      </Button>
                    </div>
                  </Section>
                )}

                {reEnrolStep === 'scan' && (
                  <Section title="Add the new device" note="Scan this, then enter a code from the NEW authenticator" pad>
                    <code
                      style={{
                        fontSize: 'var(--fs-admin-colhead)',
                        wordBreak: 'break-all',
                        color: 'var(--c-ink2)',
                      }}
                    >
                      {reEnrolUri}
                    </code>
                    <Input
                      density="admin"
                      label="Code from the new authenticator"
                      value={reEnrolCode}
                      onChange={setReEnrolCode}
                      placeholder="123456"
                    />
                    <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                      Confirming retires the old authenticator and signs out your other sessions.
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
                      <Button size="sm" disabled={!reEnrolCode || confirmReEnrol.isPending} onClick={finishReEnrol}>
                        {confirmReEnrol.isPending ? 'Confirming…' : 'Confirm new authenticator'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={endReEnrol}>
                        Cancel
                      </Button>
                    </div>
                  </Section>
                )}

                {codesStep === 'stepup' && (
                  <Section title="Prove it is you" note="Your current password and a code from your authenticator" pad>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--sp-md)' }}>
                      <Input
                        density="admin"
                        type="password"
                        label="Current password to authorise new codes"
                        value={codesPassword}
                        onChange={setCodesPassword}
                      />
                      <Input
                        density="admin"
                        label="Code from your authenticator to authorise new codes"
                        value={codesCode}
                        onChange={setCodesCode}
                        placeholder="123456"
                      />
                    </div>
                    <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                      Your unused codes stop working the moment the new set is made. Nothing else
                      changes — your authenticator and your sessions are left alone.
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
                      <Button
                        size="sm"
                        disabled={!codesPassword || !codesCode || regenerate.isPending}
                        onClick={finishRegenerate}
                      >
                        {regenerate.isPending ? 'Generating…' : 'Generate new codes'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={endRegenerate}>
                        Cancel
                      </Button>
                    </div>
                  </Section>
                )}

                {freshCodes.length > 0 && (
                  <Section title="Your new recovery codes" note="Shown once — copy them somewhere safe now" pad>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6 }}>
                      {freshCodes.map((code) => (
                        <code key={code} style={{ fontSize: 'var(--fs-admin-cell)' }}>{code}</code>
                      ))}
                    </div>
                  </Section>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-md)',
                    padding: 'var(--sp-md)',
                    border: '1px solid var(--c-border)',
                    borderRadius: 'var(--r-tile)',
                    background: 'var(--c-surface2)',
                    flexWrap: 'wrap',
                  }}
                >
                  <Icon name="list-checks" size={18} style={{ color: 'var(--c-saffron)' }} />
                  <div style={{ flex: 1, display: 'grid', minWidth: 200 }}>
                    <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink)' }}>Recovery codes</span>
                    <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                      {/* ⛔ The count is the server's `COUNT(*)`, withheld until it arrives. The
                          "8 of 10" this replaced was a fixture string telling an operator how
                          many escape hatches they had left on no evidence. */}
                      {recovery.data ? `${recovery.data.unused_count} unused · ` : ''}
                      Each code works once · shown only at generation · regenerating voids the old set
                    </span>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => setConfirm('codes')}>
                    Regenerate
                  </Button>
                </div>
              </Section>

              <Section
                title="Active sessions"
                note="Access 15 min · refresh 7 days, rotating · absolute 30 days · idle lock 30 min"
                pad={false}
              >
                {sessions.isPending ? (
                  <div style={{ padding: 'var(--sp-lg)', color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-cell)' }}>
                    Loading your sessions…
                  </div>
                ) : sessions.isError ? (
                  <div
                    role="alert"
                    style={{ padding: 'var(--sp-lg)', color: 'var(--c-error-text)', fontSize: 'var(--fs-admin-cell)' }}
                  >
                    {errorMessage(sessions.error)}
                  </div>
                ) : (
                  <div style={{ display: 'grid' }}>
                    {sessions.data.items.map((s, i) => (
                      <div
                        key={s.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--sp-md)',
                          padding: 'var(--sp-md) var(--sp-lg)',
                          borderTop: i ? '1px solid var(--c-border)' : 'none',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Icon name={deviceIcon(s.platform)} size={18} style={{ color: 'var(--c-ink3)' }} />
                        <div style={{ flex: 1, display: 'grid', minWidth: 200 }}>
                          <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink)' }}>
                            {s.device_label ?? 'Unnamed device'}
                          </span>
                          <span
                            className="mono-num"
                            style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}
                          >
                            {s.ip_city ?? 'Location unknown'} · last seen {formatDateTime(s.last_seen_at)}
                          </span>
                        </div>
                        {s.current ? (
                          <StatusChip tone="success" label="This device" />
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={revoke.isPending}
                            onClick={() =>
                              revoke.mutate(s.id, {
                                onSuccess: () => toast({ tone: 'success', message: 'That session is ended' }),
                                onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                              })
                            }
                          >
                            Revoke
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </>
          )}

          {tab === 'notify' && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-sm)',
                  background: 'var(--c-sunk)',
                  border: '1px solid var(--c-border)',
                  borderRadius: 'var(--r-tile)',
                  padding: 'var(--sp-md) var(--sp-lg)',
                  fontSize: 'var(--fs-admin-ui)',
                  color: 'var(--c-ink2)',
                  flexWrap: 'wrap',
                }}
              >
                <Icon name="bell" size={16} />
                <span style={{ flex: 1, minWidth: 260 }}>
                  These are your own alerts. Two of them are locked on — safety pages and audit-chain
                  breaks reach every admin regardless of preference.
                </span>
                <Button
                  size="sm"
                  disabled={Object.keys(pending).length === 0 || savePrefs.isPending}
                  onClick={commitPrefs}
                >
                  {savePrefs.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>

              {/* Loading, empty and failed are three different things and the operator needs to
                  know which one they are looking at — a blank list reads as "you get nothing". */}
              {prefs.isPending && <Section title="Alerts"><span>Loading your alerts…</span></Section>}
              {prefs.isError && (
                <Section title="Alerts">
                  <span style={{ color: 'var(--c-danger)' }}>
                    Your alert settings could not be loaded, so none of these switches are yours
                    yet. {errorMessage(prefs.error)}
                  </span>
                </Section>
              )}

              {groupTopics(topics).map((g) => (
                <Section key={g.group} title={g.group} note={`${g.rows.length} alerts`} pad={false}>
                  <div style={{ display: 'grid' }}>
                    {g.rows.map((row, i) => {
                      const copy = topicCopy(row.topic);
                      return (
                        <div
                          key={row.topic}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--sp-md)',
                            padding: 'var(--sp-md) var(--sp-lg)',
                            borderTop: i ? '1px solid var(--c-border)' : 'none',
                          }}
                        >
                          <div style={{ flex: 1, display: 'grid' }}>
                            <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink)' }}>{copy.label}</span>
                            <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{copy.note}</span>
                          </div>
                          {row.is_locked && <StatusChip tone="warn" label="Always on" />}
                          <Toggle
                            label={copy.label}
                            on={row.is_enabled}
                            locked={row.is_locked}
                            /* ⛔ The SERVER's reason, not a sentence written here. The console used
                               to hold both the lock and its explanation, which made the guarantee
                               a property of a client build. */
                            lockedReason={row.locked_reason ?? undefined}
                            onChange={(next) => setPending((q) => ({ ...q, [row.topic]: next }))}
                          />
                        </div>
                      );
                    })}
                  </div>
                </Section>
              ))}
            </>
          )}

          {tab === 'appearance' && (
            <>
              <Section title="Theme" note="The console is warm brown-black in dark mode, never neutral grey">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--sp-md)' }}>
                  {THEME_CHOICES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      aria-pressed={field('theme') === t.key}
                      onClick={() => edit('theme')(t.key)}
                      style={{
                        display: 'grid',
                        gap: 6,
                        textAlign: 'left',
                        cursor: 'pointer',
                        padding: 'var(--sp-md)',
                        border:
                          field('theme') === t.key
                            ? '1px solid var(--c-saffron)'
                            : '1px solid var(--c-border)',
                        background: 'var(--c-surface2)',
                        borderRadius: 'var(--r-tile)',
                      }}
                    >
                      <Icon name={t.icon} size={18} style={{ color: 'var(--c-ink3)' }} />
                      <span
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 'var(--fs-admin-card-heading)',
                          fontWeight: 'var(--fw-semibold)',
                          color: 'var(--c-ink)',
                        }}
                      >
                        {t.label}
                      </span>
                      <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{t.note}</span>
                    </button>
                  ))}
                </div>
                <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                  The theme switch lives in the top bar and is held in the URL, so a link you share
                  opens the way you saw it.
                </span>
              </Section>

              <Section
                title="Display"
                note="Density and motion — saved to your account, not the browser"
                actions={
                  <Button size="sm" disabled={!dirty || saveProfile.isPending} onClick={commitProfile}>
                    Save
                  </Button>
                }
              >
                <Select
                  density="admin"
                  label="Default table density"
                  value={labelFor(DENSITIES, field('density'))}
                  onChange={(label) => edit('density')(valueFor(DENSITIES, label))}
                  options={DENSITIES.map((c) => c.label)}
                  placeholder="Not set"
                />
                <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
                  {DISPLAY_PREFS.map((pref) => (
                    <div
                      key={pref.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-md)',
                        padding: 'var(--sp-md)',
                        border: '1px solid var(--c-border)',
                        borderRadius: 'var(--r-tile)',
                        background: 'var(--c-surface2)',
                      }}
                    >
                      <div style={{ flex: 1, display: 'grid' }}>
                        <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink)' }}>{pref.label}</span>
                        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{pref.note}</span>
                      </div>
                      <Toggle
                        label={pref.label}
                        on={profile.data?.[pref.key] ?? false}
                        onChange={(next) =>
                          saveProfile.mutate(
                            { [pref.key]: next },
                            { onError: (e) => toast({ tone: 'error', message: errorMessage(e) }) },
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </Section>
            </>
          )}

          {tab === 'keys' && (
            <Section title="Keyboard shortcuts" note="Fixed and not rebindable — an ops desk shares muscle memory">
              {SHORTCUTS.map(([group, rows]) => (
                <div key={group} style={{ display: 'grid', gap: 6 }}>
                  <KV rows={rows.map(([label, keys]) => [label, keys])} />
                  <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{group}</span>
                </div>
              ))}
            </Section>
          )}
        </div>
      </div>

      <ConfirmAction
        open={confirm === 'signout'}
        title="Sign out of every other device?"
        phrase="SIGN OUT"
        tone="warn"
        consequences={[
          `Revokes ${others.length} other ${others.length === 1 ? 'session' : 'sessions'} immediately. This one stays signed in.`,
          'Each of those devices needs password and TOTP to come back.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          signOutOthers.mutate(undefined, {
            onSuccess: () => toast({ tone: 'success', message: 'Other sessions revoked' }),
            onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
          });
        }}
        confirmLabel="Sign out others"
      />

      <ConfirmAction
        open={confirm === 'deactivate'}
        title="Request deactivation of your own account?"
        phrase="DEACTIVATE ME"
        tone="error"
        reason
        consequences={[
          'You cannot deactivate yourself — this records a request for a superadmin to act on.',
          'Your audit trail stays forever, attributed to you.',
          'Until someone acts on it, nothing changes and you keep working.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) => {
          setConfirm(null);
          requestDeactivation.mutate(reason, {
            onSuccess: () =>
              toast({ tone: 'info', message: 'Deactivation requested · a superadmin will act on it' }),
            onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
          });
        }}
        confirmLabel="Send request"
      />

      <ConfirmAction
        open={confirm === 'password'}
        title="Update your password?"
        phrase="UPDATE"
        tone="warn"
        consequences={[
          'Signs out your other sessions — this one stays.',
          'The new password is hashed with Argon2id; we never store or email it.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          changePassword.mutate(
            { current_password: currentPassword, new_password: password, totp_code: passwordCode.trim() },
            {
              onSuccess: () => {
                setPassword('');
                setConfirmation('');
                setCurrentPassword('');
                setPasswordCode('');
                toast({ tone: 'success', message: 'Password updated · other sessions signed out' });
              },
              /* The server's refusal, verbatim: it knows whether the old password was wrong, the
                 code was stale, or the new password fails a rule this screen does not enforce. */
              onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
            },
          );
        }}
        confirmLabel="Update password"
      />

      <ConfirmAction
        open={confirm === 'totp'}
        title="Re-enrol your authenticator?"
        phrase="RE-ENROL"
        tone="error"
        consequences={[
          'Your current authenticator stops working the moment the new one is confirmed.',
          'Two-factor cannot be turned off — this replaces it, it does not remove it.',
          'Have your recovery codes to hand before you start.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          setReEnrolStep('stepup');
        }}
        confirmLabel="Start re-enrolment"
      />

      <ConfirmAction
        open={confirm === 'codes'}
        title="Regenerate your recovery codes?"
        phrase="REGENERATE"
        tone="error"
        consequences={[
          'Your unused codes stop working the moment the new set is made.',
          'The new set is shown once and never again — save them before you close the dialog.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          setFreshCodes([]);
          setCodesStep('stepup');
        }}
        confirmLabel="Regenerate codes"
      />
    </>
  );
}
